import { Loader2, Play, Video } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CatalogPickerDialog } from "@/components/ui/catalog-picker-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { fetchAvailableCameraPresets } from "@/lib/camera-presets-api"
import { cn } from "@/lib/utils"
import type { CameraLevel } from "./types"

export type CameraPickerPreset = {
  id: number
  name: string
  description?: string | null
  tags?: string[]
  level?: CameraLevel
  demo_video_url?: string | null
  demo_poster_url?: string | null
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  /** Seed from parent cache to avoid empty flash after a prior load. */
  presets?: CameraPickerPreset[]
  selectedId?: number | null
  onSelect: (preset: CameraPickerPreset) => void
  onPresetsLoaded?: (presets: CameraPickerPreset[]) => void
  /** Raise above fullscreen hosts like ShotPanel (z 100/101). */
  nested?: boolean
}

const GROUP_ORDER: CameraLevel[] = ["project", "company", "system"]
/** HTMLMediaElement.HAVE_FUTURE_DATA — enough data to play through briefly */
const HAVE_FUTURE_DATA = 3

function matches(preset: CameraPickerPreset, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  if (preset.name.toLowerCase().includes(q)) return true
  if ((preset.description ?? "").toLowerCase().includes(q)) return true
  return (preset.tags ?? []).some((tag) => tag.toLowerCase().includes(q))
}

export function CameraPickerDialog({
  open,
  onOpenChange,
  projectId,
  presets: presetsProp,
  selectedId,
  onSelect,
  onPresetsLoaded,
  nested = false,
}: Props) {
  const { t } = useTranslation(["shot", "workspace"])
  const [filterText, setFilterText] = useState("")
  const [playingId, setPlayingId] = useState<number | null>(null)
  const [pinnedId, setPinnedId] = useState<number | null>(null)
  const [loadingId, setLoadingId] = useState<number | null>(null)
  const [presets, setPresets] = useState<CameraPickerPreset[]>(presetsProp ?? [])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map())
  const onPresetsLoadedRef = useRef(onPresetsLoaded)
  onPresetsLoadedRef.current = onPresetsLoaded

  useEffect(() => {
    if (presetsProp != null) setPresets(presetsProp)
  }, [presetsProp])

  useEffect(() => {
    if (!open) return

    let cancelled = false
    setListError(null)
    // Avoid empty flash when parent/cache already has rows
    setListLoading((presetsProp?.length ?? 0) === 0 && presets.length === 0)

    void fetchAvailableCameraPresets(projectId)
      .then((list) => {
        if (cancelled) return
        setPresets(list)
        setListLoading(false)
        onPresetsLoadedRef.current?.(list)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setListLoading(false)
        setListError(e instanceof Error ? e.message : t("workspace:camerasPage.loadFailed"))
      })

    return () => {
      cancelled = true
    }
    // presets/presetsProp only seed loading flag on open — intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, t])

  const grouped = useMemo(() => {
    const filtered = presets.filter((p) => matches(p, filterText))
    const map = new Map<CameraLevel, CameraPickerPreset[]>()
    for (const level of GROUP_ORDER) map.set(level, [])
    for (const preset of filtered) {
      const level = preset.level ?? "system"
      const list = map.get(level) ?? map.get("system")
      list?.push(preset)
    }
    return GROUP_ORDER.map((level) => ({ level, items: map.get(level) ?? [] })).filter((g) => g.items.length > 0)
  }, [presets, filterText])

  const clearLoading = useCallback((id: number) => {
    setLoadingId((cur) => (cur === id ? null : cur))
  }, [])

  const markLoadingIfNeeded = useCallback(
    (el: HTMLVideoElement, id: number) => {
      if (el.readyState < HAVE_FUTURE_DATA) setLoadingId(id)
      else clearLoading(id)
    },
    [clearLoading],
  )

  const resetPlayback = useCallback(() => {
    videoRefs.current.forEach((el) => el.pause())
    setPlayingId(null)
    setPinnedId(null)
    setLoadingId(null)
  }, [])

  const pauseOthers = useCallback((exceptId: number) => {
    videoRefs.current.forEach((el, id) => {
      if (id !== exceptId) el.pause()
    })
    setPinnedId((cur) => (cur !== null && cur !== exceptId ? null : cur))
    setLoadingId((cur) => (cur !== null && cur !== exceptId ? null : cur))
  }, [])

  const togglePreview = useCallback(
    (id: number) => {
      const el = videoRefs.current.get(id)
      if (!el) return
      if (!el.paused) {
        // Hover already started playback — first click pins, second pauses
        if (pinnedId !== id) {
          pauseOthers(id)
          setPlayingId(id)
          setPinnedId(id)
          return
        }
        el.pause()
        setPlayingId(null)
        setPinnedId(null)
        return
      }
      pauseOthers(id)
      markLoadingIfNeeded(el, id)
      void el.play().catch(() => {
        clearLoading(id)
      })
      setPlayingId(id)
      setPinnedId(id)
    },
    [pauseOthers, pinnedId, markLoadingIfNeeded, clearLoading],
  )

  const select = (preset: CameraPickerPreset) => {
    resetPlayback()
    onSelect(preset)
    onOpenChange(false)
  }

  const sections = useMemo(
    () =>
      grouped.map((group) => ({
        id: group.level,
        label: t(`workspace:camerasPage.sections.${group.level}`),
        items: group.items.map((preset) => {
          const selected = preset.id === selectedId
          const isPlaying = playingId === preset.id
          const isLoading = loadingId === preset.id
          const hasVideo = Boolean(preset.demo_video_url)

          return (
            <div
              key={preset.id}
              className={cn(
                "rounded-lg border p-2 transition-colors hover:bg-ui-surface-raised",
                selected ? "border-ui-accent bg-ui-accent/10" : "border-ui-border bg-ui-surface-sunken/40",
              )}
            >
              {hasVideo ? (
                <button
                  type="button"
                  className="relative aspect-video w-full cursor-pointer overflow-hidden rounded-lg bg-ui-surface-sunken"
                  aria-busy={isLoading || undefined}
                  aria-label={
                    isLoading
                      ? t("workspace:camerasPage.previewLoading")
                      : isPlaying
                        ? t("workspace:camerasPage.previewPause")
                        : t("workspace:camerasPage.previewPlay")
                  }
                  onClick={() => togglePreview(preset.id)}
                >
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current.set(preset.id, el)
                      else videoRefs.current.delete(preset.id)
                    }}
                    src={preset.demo_video_url!}
                    poster={preset.demo_poster_url ?? undefined}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                    onMouseEnter={(e) => {
                      pauseOthers(preset.id)
                      markLoadingIfNeeded(e.currentTarget, preset.id)
                      void e.currentTarget.play().catch(() => {
                        clearLoading(preset.id)
                      })
                    }}
                    onMouseLeave={(e) => {
                      if (pinnedId === preset.id) return
                      e.currentTarget.pause()
                      clearLoading(preset.id)
                    }}
                    onLoadStart={() => {
                      const el = videoRefs.current.get(preset.id)
                      if (el && !el.paused) markLoadingIfNeeded(el, preset.id)
                    }}
                    onWaiting={() => setLoadingId(preset.id)}
                    onCanPlay={() => clearLoading(preset.id)}
                    onPlaying={() => {
                      clearLoading(preset.id)
                      setPlayingId(preset.id)
                    }}
                    onPause={() => {
                      setPlayingId((cur) => (cur === preset.id ? null : cur))
                      if (pinnedId !== preset.id) clearLoading(preset.id)
                    }}
                  />
                  {isLoading ? (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
                      <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden />
                    </span>
                  ) : !isPlaying ? (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white">
                        <Play className="h-4 w-4 fill-current" />
                      </span>
                    </span>
                  ) : null}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => select(preset)}
                  className="flex aspect-video w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-ui-surface-sunken text-ui-text-muted"
                >
                  <Video className="h-5 w-5 opacity-60" />
                </button>
              )}
              <button type="button" onClick={() => select(preset)} className="mt-2 w-full cursor-pointer text-left">
                <p className="line-clamp-1 text-xs font-medium text-ui-text-primary">{preset.name}</p>
                {preset.description ? (
                  <p className="mt-0.5 line-clamp-2 text-[10px] text-ui-text-muted">{preset.description}</p>
                ) : null}
              </button>
            </div>
          )
        }),
      })),
    // select / togglePreview close over handlers — intentional while dialog open
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grouped, selectedId, playingId, pinnedId, loadingId, t, pauseOthers, markLoadingIfNeeded, clearLoading],
  )

  const emptyLabel = listLoading
    ? t("workspace:camerasPage.pickerLoading")
    : listError
      ? listError
      : presets.length === 0
        ? t("workspace:camerasPage.empty")
        : t("workspace:camerasPage.noResults")

  return (
    <CatalogPickerDialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setFilterText("")
        } else {
          resetPlayback()
        }
        onOpenChange(next)
      }}
      title={t("workspace:camerasPage.pickerTitle")}
      closeLabel={t("workspace:camerasPage.close")}
      searchValue={filterText}
      onSearchChange={setFilterText}
      searchPlaceholder={t("workspace:camerasPage.searchPlaceholder")}
      sections={sections}
      emptyLabel={emptyLabel}
      isEmpty={listError != null || (listLoading && presets.length === 0) || (!listLoading && grouped.length === 0)}
      nested={nested}
    />
  )
}
